
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Settings, Search, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import BrandLogo from "@/components/BrandLogo";


interface HeaderProps {
  onOpenShare?: () => void;
  onOpenSettings?: () => void;
  username?: string;
}

const Header = ({ onOpenShare, onOpenSettings, username }: HeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const isOwnProfile = location.pathname === '/profile';
  const isOtherUserProfile = location.pathname.startsWith('/profile/') && location.pathname !== '/profile/drafts';
  const isProfilePage = isOwnProfile || isOtherUserProfile;

  const handleSignOut = async () => {
    await signOut();
  };

  // Hide full header on own profile (ProfileTabs handles nav there)
  // Show minimal header on other users' profiles
  if (isOwnProfile) {
    return null;
  }
  
  return (
    // body already applies env(safe-area-inset-top); don't double-pad here
    <header className="bg-background border-b border-border/20 py-0 px-1 md:px-2 relative z-10">
      <div className="container mx-auto">
        {isOtherUserProfile ? (
          <div className="flex items-center justify-between h-10">
            <div className="flex items-center space-x-1">
              <Button variant="ghost" size="sm" aria-label="Go back" className="h-9 w-9 p-0 text-foreground hover:bg-muted" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Link to="/" className="flex items-center space-x-1.5 min-h-[40px]">
                <BrandLogo className="h-[18px] w-[18px]" width={18} height={18} />
                <span className="font-medium text-[13.5px] text-foreground tracking-tight">SoundCheck</span>
              </Link>
            </div>
          </div>

        ) : (
          <div className="flex items-center justify-between h-10">
            <Link to="/" className="flex items-center space-x-1.5 mr-auto -ml-1 min-h-[40px]">
              <BrandLogo className="h-[18px] w-[18px]" width={18} height={18} />
              <span className="font-medium text-[13.5px] text-foreground tracking-tight">SoundCheck</span>
            </Link>

            <div className="flex items-center gap-0.5">
              {user ? (
                <>
                  <Link to="/discover?mode=search" aria-label="Search">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted/60" aria-label="Search">
                      <Search className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-[13px] text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-3.5 w-3.5 mr-1" />
                    Sign Out
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted/60" onClick={() => navigate('/settings')} aria-label="Settings">
                    <Settings className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/discover?mode=search" aria-label="Search">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-muted/60" aria-label="Search">
                      <Search className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                  <Link to="/signin">
                    <Button variant="ghost" size="sm" className="h-8 px-3 text-[13px] text-foreground hover:bg-muted/60">
                      Sign In
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </header>
  );
};

export default Header;
